/**
 * web/src/components/approvals/ReviewQueue.tsx
 * The review queue (design_handoff_speaker_approvals §Screen 2): a full-screen
 * overlay that steps through an ordered set of pending approvals one at a
 * time with keyboard-driven Approve / Send back / Skip and a summary at the
 * end. Absorbs the retired BatchGateReview (email gates, via BatchEmailForm).
 * EMAIL GATES ONLY since 2026-08-20 — speaker cards are reviewed on the
 * Speaker Cards pipeline board (/speakers/cards), not here.
 *
 * Layout (2026-08-19, Danny): the body is the EXISTING single-gate email view
 * — one centered column at GateReview's width with the same EmailPreview
 * editor — under a slim context header. The queue chrome (top bar, pips,
 * footer) only adds batch navigation around it; no side panel.
 *
 * Decision semantics:
 *   - Approve fires the EXISTING single-gate approve endpoint per item as the
 *     queue advances — send-safety (per-approver Gmail, dry-run, placeholder
 *     guard) is unchanged. A failed approve keeps the item on-screen with the
 *     error; no phantom tallies.
 *   - Send back = reject with a required reason (RejectModal); the row lands
 *     in the list's "Sent back — needs edits" section.
 *   - Skip is NOT a decision. It's local-only: the gate stays
 *     waiting_approval and remains in the pending list on ALL exit paths
 *     (summary, ✕) — the workflow-level "sent outside Pixel" skip lives
 *     on the full GateReview page only.
 *
 * No keyboard shortcuts (2026-08-25): every action is button-only. A stray
 * keypress — e.g. "a" while focus sat outside the body editor — used to fire
 * Approve & send, so the decision hotkeys (A/S/K), arrow navigation, and
 * Esc-to-exit were removed. Navigate with the pips; close with the ✕.
 *
 * Regenerate (2026-09): a footer button recomposes the CURRENT item's draft
 * from the event's current templates/defaults via RegenerateDialog (confirm
 * first — the reviewer's edits to that draft are dropped). Nothing is sent,
 * the gate stays waiting_approval, and the item stays in the queue: the
 * stashed draft is discarded and the form remounts on the fresh server copy.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkflow } from '../../hooks/queries/useWorkflow';
import { useApproveGate } from '../../hooks/mutations/useApproveGate';
import { useRejectGate } from '../../hooks/mutations/useRejectGate';
import { useAuth } from '../../hooks/useAuth';
import { BatchEmailForm, type BatchEmailEdits, type BatchEmailSeed } from '../gates/BatchEmailForm';
import { promotionCardPinned } from '../../lib/promotionCard';
import { gatePinnedAttachments } from '../../lib/pinnedAttachments';
import { RejectModal } from '../gates/RejectModal';
import { RegenerateDialog, type RegenerateOutcome, type RegenerateTarget } from './RegenerateDialog';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { Empty } from '../shared/Empty';
import { Select } from '../shared/Select';
import { Input } from '../shared/Input';
import { Icon } from '../../lib/icons';
import { ApiClientError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { gateKey, daysWaiting, type ApprovalRow } from '../../lib/approvalsDerive';
import { SPEAKER_STEP_COUNT } from '../../lib/approvalKinds';
import { isGateData } from '../../api/types';

/** A queue decision. `skip` is queue-local — the gate stays pending. */
type Decision = 'approve' | 'back' | 'skip';

/** Extra (non-envelope) editable fields on an email gate's gateSchema. */
interface ExtraField {
  key: string;
  label: string;
  type: string;
  options?: readonly string[];
}

/** The envelope fields BatchEmailForm already owns (GateReview's list). */
const EMAIL_ENVELOPE_KEYS = new Set([
  'to',
  'cc',
  'bcc',
  'subject',
  'body',
  'attachments',
  'removedPinnedAttachments',
  'sendFromConnectionId',
]);

/**
 * Full-screen review queue over an ordered item list.
 *
 * @param items the selected approvals, in review order
 * @param onExit called on every exit path with the keys of items actually
 *        resolved server-side (approved or sent back) — skips excluded, so
 *        the caller keeps skipped rows selected/pending
 * @sideEffects approve/reject mutations
 */
export function ReviewQueue({
  items,
  onExit,
}: {
  items: ApprovalRow[];
  onExit: (resolvedKeys: string[]) => void;
}) {
  const navigate = useNavigate();
  const approve = useApproveGate();
  const reject = useRejectGate();
  const { me } = useAuth();

  const [index, setIndex] = useState(0);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [finished, setFinished] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Regenerate confirm for the current item (null = closed).
  const [regenTargets, setRegenTargets] = useState<RegenerateTarget[] | null>(null);
  // Per-item remount counter: bumped after a regenerate so the (keyed) form
  // re-seeds from the refetched gateData instead of its first-mount seed.
  const [epochs, setEpochs] = useState<Record<string, number>>({});
  // Per-item email drafts so edits persist while navigating within the queue.
  const [drafts, setDrafts] = useState<Record<string, BatchEmailEdits>>({});
  const [extraDrafts, setExtraDrafts] = useState<Record<string, Record<string, string>>>({});
  // Live form state for the CURRENT item. The edits payload lives in a REF,
  // not state: BatchEmailForm re-emits a fresh object on every render (RHF
  // watch()/getValues() return copies), so mirroring it into state re-renders
  // forever. State keeps only convergent values (a boolean + a content-compared
  // placeholder list) so the every-render re-emit bails out instead of looping.
  const emailEditsRef = useRef<BatchEmailEdits | null>(null);
  const [hasEmailEdits, setHasEmailEdits] = useState(false);
  const [placeholders, setPlaceholders] = useState<string[]>([]);

  const onEditsChange = useCallback((edits: BatchEmailEdits) => {
    emailEditsRef.current = edits;
    setHasEmailEdits(true); // same-value set bails out — no re-render loop
  }, []);
  const onPlaceholdersChange = useCallback((next: string[]) => {
    // Keep the previous array identity when contents match so React bails out.
    setPlaceholders((prev) =>
      prev.length === next.length && prev.every((p, i) => p === next[i]) ? prev : next,
    );
  }, []);

  const total = items.length;
  const current = !finished && index < total ? items[index] : null;
  const currentKey = current ? gateKey(current.gate) : '';

  const healthyConnections = useMemo(
    () => (me?.gmailConnections ?? []).filter((c) => c.lastErrorReason == null),
    [me?.gmailConnections],
  );

  // Load the CURRENT item's workflow for the email payload (+ step status).
  const wf = useWorkflow(current?.gate.workflowId);
  const step = useMemo(
    () => wf.data?.steps.find((s) => s.stepIndex === current?.gate.stepIndex),
    [wf.data, current?.gate.stepIndex],
  );

  const emailSeed = useMemo<BatchEmailSeed | null>(() => {
    if (!current) return null;
    const draft = drafts[currentKey];
    if (draft) {
      return {
        to: draft.to,
        cc: draft.cc,
        bcc: draft.bcc,
        subject: draft.subject,
        body: draft.body,
      };
    }
    const gd = step?.gateData;
    if (!isGateData(gd) || gd.kind !== 'email-gate') return null;
    return { to: gd.email.to, cc: gd.email.cc, bcc: gd.email.bcc, subject: gd.email.subject, body: gd.email.body };
    // drafts is read once per item mount — the live copy lives in emailEdits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, currentKey, step?.gateData]);

  // Promotion gates attach the monday-board speaker card on send — surface it
  // as a pinned chip WITH a thumbnail so batch reviewers see the exact card.
  // Other gates may declare server-pinned files in gateData.context (the
  // sponsor welcome email's logos).
  const pinnedAttachments = useMemo(
    () =>
      current?.gate.stepId === 'promotion'
        ? promotionCardPinned(current.gate.workflowId, step?.gateData)
        : gatePinnedAttachments(step?.gateData, current?.gate.workflowId, current?.gate.stepIndex),
    [current, step?.gateData],
  );

  const defaultSendFromId = useMemo(() => {
    const drafted = drafts[currentKey]?.sendFromConnectionId;
    if (drafted && healthyConnections.some((c) => c.id === drafted)) return drafted;
    if (healthyConnections.length === 0) return '';
    const eventId = current?.gate.workflow.eventId;
    const mapped = eventId ? me?.gmailEventDefaults?.[eventId] : undefined;
    return mapped && healthyConnections.some((c) => c.id === mapped)
      ? mapped
      : (healthyConnections[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, healthyConnections, current?.gate.workflow.eventId, me?.gmailEventDefaults]);

  // Extra editable non-envelope fields (e.g. confirmation's hotelBlock select)
  // — rendered above the email so batch review stops silently dropping them.
  const extraFields = useMemo<ExtraField[]>(() => {
    if (!current) return [];
    const raw = step?.gateSchema;
    if (!Array.isArray(raw)) return [];
    return (raw as ExtraField[]).filter(
      (f) =>
        f &&
        typeof f.key === 'string' &&
        !EMAIL_ENVELOPE_KEYS.has(f.key) &&
        f.type !== 'readonly' &&
        (f as { editable?: boolean }).editable !== false,
    );
  }, [current, step?.gateSchema]);
  const extraValues = useMemo(() => extraDrafts[currentKey] ?? {}, [extraDrafts, currentKey]);

  // ---- Decisions -------------------------------------------------------------

  const tally = useMemo(() => {
    const values = Object.values(decisions);
    return {
      approved: values.filter((d) => d === 'approve').length,
      back: values.filter((d) => d === 'back').length,
      skipped: values.filter((d) => d === 'skip').length,
    };
  }, [decisions]);
  const decided = tally.approved + tally.back + tally.skipped;

  /** Keys resolved server-side. Skips are NOT resolved — they stay pending.
   *  Shared by both exit paths (summary, ✕). */
  const resolvedKeys = useCallback(
    () =>
      Object.entries(decisions)
        .filter(([, d]) => d !== 'skip')
        .map(([k]) => k),
    [decisions],
  );

  const exit = useCallback(() => onExit(resolvedKeys()), [onExit, resolvedKeys]);

  const goTo = useCallback((i: number) => {
    setActionError(null);
    emailEditsRef.current = null;
    setHasEmailEdits(false);
    setPlaceholders([]);
    setIndex(i);
  }, []);

  const advance = useCallback(
    (decision: Decision) => {
      if (!current) return;
      setDecisions((prev) => ({ ...prev, [currentKey]: decision }));
      if (index + 1 >= total) {
        setFinished(true);
      } else {
        goTo(index + 1);
      }
    },
    [current, currentKey, index, total, goTo],
  );

  const stashDrafts = useCallback(() => {
    if (!current) return;
    const edits = emailEditsRef.current;
    if (edits) setDrafts((d) => ({ ...d, [currentKey]: edits }));
  }, [current, currentKey]);

  const jumpTo = useCallback(
    (i: number) => {
      if (i === index || finished) return;
      stashDrafts();
      goTo(i);
    },
    [index, finished, stashDrafts, goTo],
  );

  const openRegenerate = useCallback(() => {
    if (!current) return;
    setRegenTargets([
      {
        workflowId: current.gate.workflowId,
        stepIndex: current.gate.stepIndex,
        label: `${current.kind.verb} · ${current.speaker}`,
      },
    ]);
  }, [current]);

  // After a successful regenerate the dialog has already refetched the
  // workflow, so dropping the stashed draft + bumping the epoch remounts the
  // form on the fresh server copy. A failure keeps everything as it was.
  const onRegenerateClose = useCallback(
    (outcomes: RegenerateOutcome[]) => {
      setRegenTargets(null);
      const fresh = outcomes.filter((o) => o.ok).map((o) => gateKey(o));
      if (fresh.length === 0) return;
      setDrafts((d) => {
        const next = { ...d };
        fresh.forEach((k) => delete next[k]);
        return next;
      });
      setExtraDrafts((d) => {
        const next = { ...d };
        fresh.forEach((k) => delete next[k]);
        return next;
      });
      if (fresh.includes(currentKey)) {
        emailEditsRef.current = null;
        setHasEmailEdits(false);
        setPlaceholders([]);
        setActionError(null);
      }
      setEpochs((e) => {
        const next = { ...e };
        fresh.forEach((k) => (next[k] = (next[k] ?? 0) + 1));
        return next;
      });
    },
    [currentKey],
  );

  const alreadyActioned =
    current != null && !wf.isPending && step != null && step.status !== 'waiting_approval';
  const emailReady = current != null && !alreadyActioned && emailSeed != null;
  const busy = approve.isPending || reject.isPending;

  const approveDisabled = current
    ? busy || !emailReady || !hasEmailEdits || placeholders.length > 0
    : true;

  const onApprove = useCallback(() => {
    if (!current || approveDisabled) return;
    setActionError(null);
    const emailEdits = emailEditsRef.current;
    if (!emailEdits) return;
    // Merge extra non-envelope fields into the envelope edits; omit empties
    // so an untouched optional select doesn't overwrite server defaults.
    const extras = Object.fromEntries(
      Object.entries(extraValues).filter(([, v]) => v !== ''),
    );
    approve.mutate(
      {
        workflowId: current.gate.workflowId,
        stepIndex: current.gate.stepIndex,
        edits: { ...(emailEdits as Record<string, unknown>), ...extras },
      },
      {
        onSuccess: () => advance('approve'),
        onError: (err) =>
          setActionError(
            err instanceof ApiClientError
              ? err.message
              : 'Approve failed — retry, or skip this one and continue.',
          ),
      },
    );
  }, [current, approveDisabled, extraValues, approve, advance]);

  const onSendBack = useCallback(
    (reason: string) => {
      if (!current) return;
      reject.mutate(
        { workflowId: current.gate.workflowId, stepIndex: current.gate.stepIndex, reason },
        {
          onSuccess: () => {
            setRejectOpen(false);
            advance('back');
          },
          onError: (err) => {
            setRejectOpen(false);
            setActionError(
              err instanceof ApiClientError ? err.message : 'Send back failed — try again.',
            );
          },
        },
      );
    },
    [current, reject, advance],
  );

  // Scroll the body back to top on every item change.
  useEffect(() => {
    document.getElementById('rq-body')?.scrollTo({ top: 0 });
  }, [index]);

  // ---- Render -------------------------------------------------------------------

  const pipClass = (i: number): string => {
    const d = decisions[gateKey(items[i].gate)];
    if (!finished && i === index) return 'bg-accent';
    if (d === 'approve') return 'bg-completed';
    if (d === 'back') return 'bg-failed';
    if (d === 'skip') return 'bg-border-strong opacity-70';
    return 'bg-border';
  };

  if (finished) {
    return (
      <Overlay>
        <div className="mx-auto flex w-full max-w-[560px] flex-col gap-5 px-6 py-16">
          <div className="flex items-center gap-2.5">
            <Icon name="check" size={22} className="text-completed" />
            <h1 className="text-[22px] font-semibold">Queue complete</h1>
          </div>
          <p className="text-[13px] text-text-muted">
            {tally.approved > 0
              ? `${tally.approved} action${tally.approved === 1 ? '' : 's'} fired.`
              : 'No actions fired.'}{' '}
            {tally.skipped > 0 && 'Skipped items stay in the approvals list.'}
          </p>
          <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-bg">
            {(
              [
                ['bg-completed', tally.approved, 'Approved & sent', 'actions fired'],
                ['bg-failed', tally.back, 'Sent back', 'kept for edit & resubmit'],
                ['bg-border-strong', tally.skipped, 'Skipped', 'stay in the list'],
              ] as const
            )
              .filter(([, count]) => count > 0)
              .map(([dot, count, label, note]) => (
                <div key={label} className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0">
                  <span className={cn('h-2 w-2 rounded-full', dot)} />
                  <span className="w-8 text-[15px] font-semibold tabular-nums">{count}</span>
                  <span className="text-[13px] font-medium text-text">{label}</span>
                  <span className="ml-auto text-[12px] text-text-subtle">{note}</span>
                </div>
              ))}
          </div>
          <div>
            <Button variant="primary" onClick={exit}>
              Back to approvals
            </Button>
          </div>
        </div>
      </Overlay>
    );
  }

  if (!current) {
    // Empty queue (defensive — the page never opens one with zero items).
    return (
      <Overlay>
        <div className="p-10">
          <Empty
            icon={<Icon name="approval" size={20} />}
            title="Nothing to review"
            description="This queue is empty."
            action={
              <Button variant="primary" onClick={exit}>
                Back to approvals
              </Button>
            }
          />
        </div>
      </Overlay>
    );
  }

  const days = daysWaiting(current.gate.createdAt, new Date());
  const stepTag = `Step ${current.kind.stepIndex + 1} of ${current.kind.stepCount ?? SPEAKER_STEP_COUNT} · ${current.kind.label}`;

  return (
    <Overlay>
      {/* Top bar */}
      <div className="flex items-center gap-3.5 border-b border-border bg-bg px-5 py-3">
        <button
          type="button"
          aria-label="Exit review queue"
          onClick={exit}
          className="flex h-7 w-7 items-center justify-center rounded-md border border-border-strong text-text-muted hover:bg-bg-hover"
        >
          <Icon name="x" size={14} />
        </button>
        <div className="text-[13px]">
          <span className="font-semibold">{index + 1}</span>
          <span className="text-text-subtle"> of {total}</span>
        </div>
        {/* Progress measures DECISIONS MADE, not position — scrubbing backwards
            doesn't inflate it. */}
        <div className="h-1 max-w-[420px] flex-1 overflow-hidden rounded-full bg-bg-muted">
          <div
            className="h-full rounded-full bg-accent transition-[width] duration-250"
            style={{ width: `${total > 0 ? Math.round((decided / total) * 100) : 0}%` }}
          />
        </div>
        <div className="ml-auto text-[11.5px] text-text-subtle">
          <strong className="font-semibold text-text-muted tabular-nums">{tally.approved}</strong> approved ·{' '}
          <strong className="font-semibold text-text-muted tabular-nums">{tally.back}</strong> sent back ·{' '}
          <strong className="font-semibold text-text-muted tabular-nums">{tally.skipped}</strong> skipped
        </div>
      </div>

      {/* Pip rail — click a pip to revisit an item without leaving the queue. */}
      <div className="flex gap-[3px] overflow-x-auto border-b border-border bg-bg px-5 py-2">
        {items.map((it, i) => (
          <button
            key={gateKey(it.gate)}
            type="button"
            aria-label={`Go to item ${i + 1}: ${it.kind.verb} for ${it.speaker}`}
            onClick={() => jumpTo(i)}
            className={cn('h-[5px] w-4 shrink-0 rounded-sm', pipClass(i))}
          />
        ))}
      </div>

      {/* Body — one centered column at the SAME width as the single-gate page
          (GateReview's max-w-[880px]), so the familiar email view IS the
          review; the queue chrome just wraps prev/next navigation around it. */}
      <div id="rq-body" className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[880px] px-6 py-5 pb-10">
          {/* Slim context header — who + what, with the full-review escape hatch. */}
          <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h1 className="text-[17px] font-semibold tracking-[-0.01em]">{current.kind.verb}</h1>
            <span className="text-[13px] text-text-muted">
              for <span className="font-medium text-text">{current.speaker}</span>
              {current.company && <> · {current.company}</>}
            </span>
            {current.returning && <Badge variant="role">returning</Badge>}
            <span className="ml-auto flex items-center gap-3 text-[12px] text-text-subtle">
              <Badge variant="default">{stepTag}</Badge>
              <span>{days === 0 ? 'Opened today' : `Waiting ${days} day${days === 1 ? '' : 's'}`}</span>
              <button
                type="button"
                className="font-semibold text-accent hover:underline"
                onClick={() => {
                  exit();
                  navigate(`/approvals/${current.gate.workflowId}/${current.gate.stepIndex}`);
                }}
              >
                Open full review →
              </button>
            </span>
          </div>

          {/* Consequence — the one line the operator relies on. */}
          <p className="mb-4 text-[12.5px] text-text-muted">
            <strong className="font-semibold text-text">On approve:</strong> {current.kind.what}
          </p>

          {/* Payload */}
          <div className="mt-4">
            {wf.isPending ? (
                <div className="py-10 text-center text-[13px] text-text-subtle">Loading email…</div>
              ) : alreadyActioned ? (
                <Empty
                  icon={<Icon name="info" size={20} />}
                  title="Already actioned"
                  description={`This gate is ${step?.status.replace('_', ' ')} — nothing to review. Continue to the next one.`}
                  action={
                    <Button variant="primary" onClick={() => advance('skip')}>
                      Next <Icon name="chevron-right" size={12} />
                    </Button>
                  }
                />
              ) : !emailSeed ? (
                <Empty
                  icon={<Icon name="info" size={20} />}
                  title="Can't review this inline"
                  description="This gate isn't a plain email. Open the full review to action it."
                  action={
                    <Button
                      variant="primary"
                      onClick={() => {
                        exit();
                        navigate(`/approvals/${current.gate.workflowId}/${current.gate.stepIndex}`);
                      }}
                    >
                      Open full review
                    </Button>
                  }
                />
              ) : (
                <>
                  {extraFields.length > 0 && (
                    <div className="mb-4 flex flex-col gap-3 rounded-lg border border-border bg-bg p-4">
                      {extraFields.map((f) => (
                        <label key={f.key} className="flex flex-col gap-1 text-[12px] font-medium text-text-muted">
                          {f.label}
                          {f.type === 'select' && Array.isArray(f.options) ? (
                            <Select
                              value={extraValues[f.key] ?? ''}
                              onChange={(e) =>
                                setExtraDrafts((d) => ({
                                  ...d,
                                  [currentKey]: { ...d[currentKey], [f.key]: e.target.value },
                                }))
                              }
                            >
                              <option value="">—</option>
                              {f.options.map((o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            <Input
                              value={extraValues[f.key] ?? ''}
                              onChange={(e) =>
                                setExtraDrafts((d) => ({
                                  ...d,
                                  [currentKey]: { ...d[currentKey], [f.key]: e.target.value },
                                }))
                              }
                            />
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                  {/* Keyed per gate so switching items remounts a fresh form
                      seeded from that gate's email (or its stashed draft). */}
                  <BatchEmailForm
                    key={`${currentKey}:${epochs[currentKey] ?? 0}`}
                    seed={emailSeed}
                    approverName={me?.name}
                    healthyConnections={healthyConnections}
                    defaultSendFromId={defaultSendFromId}
                    pinnedAttachments={pinnedAttachments}
                    onEditsChange={onEditsChange}
                    onPlaceholdersChange={onPlaceholdersChange}
                  />
                </>
              )}
          </div>

          {actionError && (
            <div
              role="alert"
              className="mt-4 flex items-start gap-2.5 rounded-lg border border-failed-border bg-failed-soft px-4 py-3 text-[13px] text-failed"
            >
              <Icon name="alert" size={16} className="mt-0.5 shrink-0" />
              <div>{actionError}</div>
            </div>
          )}
        </div>
      </div>

      {/* Footer — actions bottom-right with Approve rightmost, matching the
          sponsor GateReview convention. Decisions are button-only (no hotkeys)
          so a stray keypress can never approve/send an email. */}
      <div className="flex flex-wrap items-center gap-3 border-t border-border bg-bg px-5 py-3">
        <Button
          variant="ghost"
          onClick={openRegenerate}
          disabled={busy || !emailReady}
          title="Rebuild this draft from the current templates and defaults — nothing is sent"
        >
          <Icon name="retry" size={12} />
          Regenerate
        </Button>
        <span className="flex-1" />
        <Button variant="ghost" onClick={() => advance('skip')} disabled={busy}>
          Skip
        </Button>
        <Button
          variant="default"
          onClick={() => setRejectOpen(true)}
          disabled={busy}
          className="border-failed-border text-failed hover:bg-failed-soft"
        >
          Send back
        </Button>
        {placeholders.length > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-waiting-border bg-waiting-soft px-3 py-1 text-[12px] font-semibold text-waiting">
            <Icon name="alert" size={12} />
            {placeholders.length} placeholder{placeholders.length === 1 ? '' : 's'} to fill
          </span>
        )}
        <Button
          variant="primary"
          onClick={onApprove}
          disabled={approveDisabled}
          title={
            placeholders.length > 0
              ? `Fill the remaining placeholder${placeholders.length === 1 ? '' : 's'} first: ${placeholders.join(', ')}`
              : undefined
          }
        >
          <Icon name="send" size={12} />
          Approve & send
        </Button>
      </div>

      <RejectModal
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onConfirm={onSendBack}
        isSubmitting={reject.isPending}
      />
      <RegenerateDialog targets={regenTargets} userId={me?.id} onClose={onRegenerateClose} />
    </Overlay>
  );
}

/** The fixed full-screen shell: column of top bar / pips / body / footer. */
function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-bg-soft" role="dialog" aria-modal="true">
      {children}
    </div>
  );
}
